<template>
	<div class="room-filters">
		<AppList :items="elements">
			<template #status-type="{item}">
				<div class="row space-between centered list-row">
					<p>{{ item.title }}</p>
					<div class="buttons-container">
						<div class="button-container" v-for="status in statuses" @click="toggleStatusFilter(status)" :class="{active: isStatus(status)}">{{ getString(status) }}</div>
					</div>
				</div>
			</template>
			<template #experiences="{item}">
				<div class="row space-between centered list-row">
					<p>{{ item.title }}</p>
					<div class="buttons-container">
						<div class="button-container" v-for="experience in experiences" @click="toggleExperienceFilter(experience.experience_id)" :class="{active: isExperience(experience.experience_id)}">{{ getString(experience.name) }}</div>
					</div>
				</div>
			</template>
		</AppList>
	</div>
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import {useRoomsStore, useRoomsOrdersStore, useUserStore, useExperiencesStore} from "@/console-new/stores";
import {useLanguageStore} from "@/shared/stores";
import AppButton from "@/console-signup-speedy/App.vue";
import {formatDateOnly} from "@/shared/helpers/dates";

const standardStatuses = ["confirmed", "arrived"];
const allStatuses = ["confirmed", "arrived", "noshow", "canceled", "finished"];

export default {
	setup() {
		const roomsStore = useRoomsStore();
		const roomsOrdersStore = useRoomsOrdersStore();
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const experiencesStore = useExperiencesStore();
		return {roomsStore, getString: languageStore.getString, roomsOrdersStore, userStore, experiencesStore};
	},
	name: "ReservationsFilters",
	components: {AppButton, AppList},
	emits: ["close"],
	data() {
		return {
			statuses: ["confirmed", "arrived", "noshow", "canceled", "finished"],
		}
	},
	computed: {
		experiences() {
			return this.experiencesStore.hasExperiences ? this.experiencesStore.businessExperiences : [];
		},
		elements() {
			return [
				{
					title: this.getString("status"),
					customSlot: "status-type",
				},
				{
					title: this.getString("experiences"),
					customSlot: "experiences",
				},
			]
		},
		defaultStatuses() {
			return this.roomsOrdersStore.reservationsSelectedDate && formatDateOnly(this.roomsOrdersStore.reservationsSelectedDate) < formatDateOnly(new Date()) ? allStatuses : standardStatuses;
		},
	},
	methods: {
		toggleStatusFilter(status) {
			if(!this.roomsOrdersStore.reservationsFilters.hasOwnProperty("status")) {
				this.roomsOrdersStore.reservationsFilters.status = this.defaultStatuses;
			}
			if (this.isStatus(status)) {
				this.roomsOrdersStore.reservationsFilters.status = this.roomsOrdersStore.reservationsFilters.status.filter((s) => s !== status);
			} else {
				if(!this.roomsOrdersStore.reservationsFilters.status.includes(status)) {
					this.roomsOrdersStore.reservationsFilters.status.push(status);
				}
			}
		},
		isStatus(status) {
			if(this.roomsOrdersStore.reservationsFilters.hasOwnProperty("status") && this.roomsOrdersStore.reservationsFilters.status) {
				return this.roomsOrdersStore.reservationsFilters.status.includes(status);
			} else {
				return this.defaultStatuses.includes(status);
			}
		},
		toggleExperienceFilter(experienceId) {
			if(!this.roomsOrdersStore.reservationsFilters.hasOwnProperty("experiences")) {
				this.roomsOrdersStore.reservationsFilters.experiences = this.experiences.map((e) => e.experience_id);
			}
			if (this.isExperience(experienceId)) {
				this.roomsOrdersStore.reservationsFilters.experiences = this.roomsOrdersStore.reservationsFilters.experiences.filter((e) => e !== experienceId);
			} else {
				if(!this.roomsOrdersStore.reservationsFilters.experiences.includes(experienceId)) {
					this.roomsOrdersStore.reservationsFilters.experiences.push(experienceId);
				}
			}
		},
		isExperience(experienceId) {
			if(this.roomsOrdersStore.reservationsFilters.hasOwnProperty("experiences") && this.roomsOrdersStore.reservationsFilters.experiences) {
				return this.roomsOrdersStore.reservationsFilters.experiences.includes(experienceId);
			} else {
				return true;
			}
		},
	},
	watch: {
		"roomsOrdersStore.reservationsSelectedDate"(newVal, oldVal) {
			if (newVal && newVal !== oldVal) {
				if(this.roomsOrdersStore.reservationsFilters.hasOwnProperty("status") && this.roomsOrdersStore.reservationsFilters.status) {
					//if the date is in the past and the status is the default one, set it to all statuses
					if (newVal < new Date() && oldVal > new Date() && arraysEqualIgnoreOrder(this.roomsOrdersStore.reservationsFilters.status, standardStatuses)) {
						this.roomsOrdersStore.reservationsFilters.status = allStatuses;
					}
					// else if the date is in the future and the status is all statuses, set it to default
					else if (newVal > new Date() && oldVal < new Date() && arraysEqualIgnoreOrder(this.roomsOrdersStore.reservationsFilters.status, allStatuses)) {
						this.roomsOrdersStore.reservationsFilters.status = standardStatuses;
					}
				}
			}
		},
	}
}

function arraysEqualIgnoreOrder(a, b) {
	if (a.length !== b.length) return false;
	const sa = [...a].sort();
	const sb = [...b].sort();
	return sa.every((val, idx) => val === sb[idx]);
}
</script>

<style scoped lang="scss">
.list-row {
	gap: 10px;
}
.buttons-container {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 10px;
	margin-left: auto;
	flex-wrap: wrap;

	.button-container {
		padding: 8px 10px;
		border-radius: 13px;
		cursor: pointer;
		border: 1px solid var(--color-primary);
		color: var(--color-primary);
		font-size: 14px;
		font-weight: 500;
		max-width: 120px;
		@include truncateString();

		&.active {
			background-color: var(--color-active-button);
			color: var(--color-active-button-text);
		}
	}
}
</style>